import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import React, { useState } from 'react';

import { useClientPluginUsage } from '@/sections/customerManagement/components/masterDashboard/hooks/useClientPluginUsage';
import { useSetClientUsage } from '@/sections/customerManagement/components/masterDashboard/hooks/useEnrichmentMutations';

interface LimitConfigModalProps {
  client: string;
  plugin: string | null;
  pluginName: string;
  onClose: () => void;
}

export const LimitConfigModal: React.FC<LimitConfigModalProps> = ({
  client,
  plugin,
  pluginName,
  onClose,
}) => {
  const { data: usage } = useClientPluginUsage(client, plugin ?? '', !!plugin);
  const { mutateAsync: setUsage, isPending } = useSetClientUsage();

  const [limitOverride, setLimitOverride] = useState<string | null>(null);
  const [headroomOverride, setHeadroomOverride] = useState<string | null>(null);

  const limit = limitOverride ?? String(usage?.limit ?? 0);
  const headroom = headroomOverride ?? String(usage?.headroom ?? 0);

  const handleSave = async () => {
    if (!plugin) return;
    try {
      await setUsage({
        client,
        plugin,
        limit: Number(limit),
        headroom: Number(headroom),
      });
      onClose();
    } catch {
      // Mutation hook surfaces the error via toast/defaultErrorMessage.
    }
  };

  return (
    <Dialog open={!!plugin} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-chariot-background-primary border-chariot-stroke w-full max-w-md rounded-lg border p-6">
          <DialogTitle className="text-chariot-text-primary mb-4 text-base font-semibold">
            Configure {pluginName} Limits
          </DialogTitle>

          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-chariot-text-secondary text-xs font-medium">
                Credit Limit
              </span>
              <input
                type="number"
                min={0}
                value={limit}
                onChange={e => setLimitOverride(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary rounded border px-3 py-2 text-sm outline-none"
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-chariot-text-secondary text-xs font-medium">
                Headroom (concurrent reservation buffer)
              </span>
              <input
                type="number"
                min={0}
                value={headroom}
                onChange={e => setHeadroomOverride(e.target.value)}
                className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary rounded border px-3 py-2 text-sm outline-none"
              />
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="text-chariot-text-secondary hover:text-chariot-text-primary rounded px-4 py-2 text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isPending}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
